import { useEffect, useState } from "react";
import { Wallet, HandCoins, Receipt, Boxes, TrendingUp, TrendingDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { api, fmtRp, fmtDate } from "@/lib/api";

const Card = ({ title, value, icon: Icon, tone, testId, delay }) => (
  <div className={`card-aqua p-4 rise ${delay}`} data-testid={testId}>
    <div className="flex items-center justify-between">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</div>
      <Icon className={`h-4 w-4 ${tone}`} />
    </div>
    <div className={`mt-2 text-xl font-extrabold font-mono tracking-tight ${tone}`}>{fmtRp(value)}</div>
  </div>
);

export function SummaryTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/accounting/summary").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-slate-500">Memuat ringkasan keuangan...</div>;
  return (
    <div data-testid="acc-summary">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <Card title="Kas & Bank" value={s.cash_bank} icon={Wallet} tone="text-sky-700" testId="acc-kpi-cash" delay="rise-1" />
        <Card title="Nilai Persediaan" value={s.inventory_value} icon={Boxes} tone="text-slate-900" testId="acc-kpi-inventory" delay="rise-2" />
        <Card title="Piutang Usaha" value={s.receivable} icon={HandCoins} tone="text-emerald-600" testId="acc-kpi-receivable" delay="rise-3" />
        <Card title="Hutang Usaha" value={s.payable} icon={Receipt} tone="text-rose-600" testId="acc-kpi-payable" delay="rise-4" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-4">
        <div className="bg-slate-900 rounded-2xl p-5 text-white rise rise-2" data-testid="acc-month-card">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Bulan ini</div>
          <div className="mt-4 space-y-3">
            <div className="flex justify-between items-center"><span className="inline-flex items-center gap-2 text-sm text-slate-300"><TrendingUp className="h-4 w-4 text-emerald-400" /> Pendapatan</span><span className="font-mono font-bold text-emerald-300">{fmtRp(s.revenue_month)}</span></div>
            <div className="flex justify-between items-center"><span className="inline-flex items-center gap-2 text-sm text-slate-300"><TrendingDown className="h-4 w-4 text-rose-400" /> Beban</span><span className="font-mono font-bold text-rose-300">{fmtRp(s.expense_month)}</span></div>
            <div className="border-t border-slate-700 pt-3 flex justify-between items-center"><span className="text-sm font-semibold">Laba (Rugi) Bersih</span><span className={`font-mono text-xl font-extrabold ${s.net_month >= 0 ? "text-sky-300" : "text-rose-300"}`} data-testid="acc-net-month">{fmtRp(s.net_month)}</span></div>
          </div>
          <div className="mt-6 text-xs text-slate-400">{s.journal_count} entri jurnal • {s.account_count} akun</div>
        </div>
        <div className="card-aqua p-5 xl:col-span-2 rise rise-3">
          <h3 className="font-semibold text-slate-800">Pendapatan vs Beban</h3>
          <p className="text-xs text-slate-500 mb-3">6 bulan terakhir</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={s.series}>
              <CartesianGrid vertical={false} stroke="#E0F2FE" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Math.round(v / 1e6)}jt`} />
              <Tooltip formatter={(v) => fmtRp(v)} cursor={{ fill: "#F0F9FF" }} contentStyle={{ borderRadius: 12, border: "1px solid #BAE6FD" }} />
              <Legend iconType="circle" />
              <Bar dataKey="pendapatan" name="Pendapatan" fill="#0284C7" radius={[6, 6, 0, 0]} />
              <Bar dataKey="beban" name="Beban" fill="#FCA5A5" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="card-aqua p-5 mt-4 rise rise-4">
        <h3 className="font-semibold text-slate-800 mb-3">Jurnal Terbaru</h3>
        <table className="w-full text-sm">
          <thead className="table-head"><tr><th className="text-left px-3 py-2">Tanggal</th><th className="text-left px-3 py-2">No. Jurnal</th><th className="text-left px-3 py-2">Deskripsi</th><th className="text-left px-3 py-2">Sumber</th><th className="text-right px-3 py-2">Total</th></tr></thead>
          <tbody>
            {s.recent_journals.map((j) => (
              <tr key={j.id} className="border-t border-sky-50">
                <td className="px-3 py-2 text-slate-600">{fmtDate(j.date)}</td>
                <td className="px-3 py-2 font-mono text-xs text-sky-800">{j.entry_no}</td>
                <td className="px-3 py-2 text-slate-800">{j.description}</td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${j.source === "stock" ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-600"}`}>{j.source === "stock" ? "Otomatis Stok" : "Manual"}</span></td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{fmtRp(j.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
